import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { TierGate } from "@/components/TierGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dices, ChevronDown, ChevronUp, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import dmScreenData from "@/data/dmScreenData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sections = [
  { key: "tavernNames", title: "Tavern Names", type: "list" },
  { key: "jobBoardQuests", title: "Job Board Quests", type: "list" },
  { key: "dungeonLocations", title: "Dungeon Locations", type: "list" },
  { key: "dungeonTypes", title: "Dungeon Types", type: "list" },
  { key: "rumors", title: "Rumors & Gossip", type: "list" },
  { key: "curses", title: "Curses", type: "list" },
  { key: "diseases", title: "Diseases", type: "list" },
  { key: "insanityEffects", title: "Insanity Effects", type: "list" },
  { key: "injuries", title: "Injuries", type: "list" },
  { key: "thriftStoreItems", title: "Thrift Store Items", type: "list" },
  { key: "shopTypes", title: "Shop Types", type: "list" },
  { key: "names", title: "Random Names", type: "names" },
  { key: "travelPace", title: "Travel Pace", type: "table" },
  { key: "services", title: "Services & Costs", type: "table" },
  { key: "difficultyClasses", title: "Difficulty Classes", type: "table" },
  { key: "magicItemPricing", title: "Magic Item Pricing", type: "pricing" },
];

const RandomResult = ({ result, onCopy }) => {
  if (!result) return null;
  return (
    <div className="mt-3 p-3 bg-primary/10 border border-primary/30 rounded-sm" data-testid="random-result">
      <p className="text-foreground text-sm font-medium">{result}</p>
      <Button
        variant="ghost"
        size="sm"
        className="mt-1 h-6 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => { navigator.clipboard.writeText(result); toast.success("Copied!"); }}
        data-testid="copy-result-button"
      >
        <Copy className="w-3 h-3 mr-1" /> Copy
      </Button>
    </div>
  );
};

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const EncounterGenerator = () => {
  const [result, setResult] = useState(null);

  const rollAll = () => {
    const raceKeys = ["humanMale", "humanFemale", "elfMale", "elfFemale", "dwarfMale", "dwarfFemale", "halflingMale", "halflingFemale"];
    const raceKey = pick(raceKeys);
    const firstName = pick(dmScreenData.names[raceKey]);
    const lastName = raceKey.startsWith("human") && dmScreenData.names.humanLast?.length
      ? ` ${pick(dmScreenData.names.humanLast)}` : "";

    setResult({
      tavern: pick(dmScreenData.tavernNames),
      quest: pick(dmScreenData.jobBoardQuests),
      npc: `${firstName}${lastName}`,
      dungeon: pick(dmScreenData.dungeonLocations),
      dungeonType: pick(dmScreenData.dungeonTypes),
      rumor: pick(dmScreenData.rumors),
      shop: pick(dmScreenData.shopTypes),
    });
  };

  const copyAll = () => {
    if (!result) return;
    const text = `Tavern: ${result.tavern}\nQuest: ${result.quest}\nNPC: ${result.npc}\nDungeon: ${result.dungeon} (${result.dungeonType})\nRumor: ${result.rumor}\nShop: ${result.shop}`;
    navigator.clipboard.writeText(text);
    toast.success("Encounter copied!");
  };

  return (
    <Card className="bg-card border-primary/30 rounded-sm mb-6" data-testid="encounter-generator">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-serif text-lg text-foreground">Encounter Generator</h2>
            <p className="text-xs text-muted-foreground">Roll everything at once for instant session prep</p>
          </div>
          <Button
            onClick={rollAll}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
            data-testid="roll-all-button"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Roll All
          </Button>
        </div>
        {result && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="encounter-result">
            <div className="bg-muted/20 p-3 rounded-sm border border-border/50">
              <p className="text-[10px] uppercase tracking-wider text-primary mb-1">Tavern</p>
              <p className="text-sm text-foreground">{result.tavern}</p>
            </div>
            <div className="bg-muted/20 p-3 rounded-sm border border-border/50">
              <p className="text-[10px] uppercase tracking-wider text-primary mb-1">Quest</p>
              <p className="text-sm text-foreground">{result.quest}</p>
            </div>
            <div className="bg-muted/20 p-3 rounded-sm border border-border/50">
              <p className="text-[10px] uppercase tracking-wider text-primary mb-1">NPC</p>
              <p className="text-sm text-foreground">{result.npc}</p>
            </div>
            <div className="bg-muted/20 p-3 rounded-sm border border-border/50">
              <p className="text-[10px] uppercase tracking-wider text-primary mb-1">Dungeon</p>
              <p className="text-sm text-foreground">{result.dungeon} <span className="text-muted-foreground">({result.dungeonType})</span></p>
            </div>
            <div className="bg-muted/20 p-3 rounded-sm border border-border/50">
              <p className="text-[10px] uppercase tracking-wider text-primary mb-1">Rumor</p>
              <p className="text-sm text-foreground">{result.rumor}</p>
            </div>
            <div className="bg-muted/20 p-3 rounded-sm border border-border/50">
              <p className="text-[10px] uppercase tracking-wider text-primary mb-1">Shop</p>
              <p className="text-sm text-foreground">{result.shop}</p>
            </div>
            <div className="md:col-span-2 lg:col-span-3 flex justify-end">
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={copyAll} data-testid="copy-all-button">
                <Copy className="w-3 h-3 mr-1" /> Copy All
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CollapsibleSection = ({ title, children, defaultOpen = false, sectionKey }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="bg-card border-border rounded-sm" data-testid={`dm-section-${sectionKey}`}>
      <button
        className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/20 transition-colors"
        onClick={() => setOpen(!open)}
        data-testid={`toggle-${sectionKey}`}
      >
        <h3 className="font-serif text-lg text-foreground">{title}</h3>
        {open ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
      </button>
      {open && <CardContent className="pt-0 pb-4 px-4">{children}</CardContent>}
    </Card>
  );
};

const ListSection = ({ data, sectionKey }) => {
  const [randomResult, setRandomResult] = useState(null);
  const roll = useCallback(() => {
    const item = data[Math.floor(Math.random() * data.length)];
    setRandomResult(item);
  }, [data]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Button
          onClick={roll}
          size="sm"
          className="bg-primary/20 text-primary hover:bg-primary/30 rounded-sm"
          data-testid={`randomize-${sectionKey}`}
        >
          <Dices className="w-4 h-4 mr-1" /> Roll Random
        </Button>
        <span className="text-xs text-muted-foreground">{data.length} entries</span>
      </div>
      <RandomResult result={randomResult} />
      <div className="mt-3 max-h-64 overflow-y-auto space-y-1 pr-2 scrollbar-thin">
        {data.map((item, i) => (
          <p key={i} className="text-sm text-muted-foreground py-1 border-b border-border/50 last:border-0">
            <span className="text-foreground/40 mr-2 text-xs">{i + 1}.</span>
            {item}
          </p>
        ))}
      </div>
    </div>
  );
};

const NamesSection = ({ data }) => {
  const [randomResult, setRandomResult] = useState(null);
  const [selectedRace, setSelectedRace] = useState(null);

  const raceLabels = {
    humanMale: "Human Male",
    humanFemale: "Human Female",
    humanLast: "Human Surname",
    elfMale: "Elf Male",
    elfFemale: "Elf Female",
    dwarfMale: "Dwarf Male",
    dwarfFemale: "Dwarf Female",
    halflingMale: "Halfling Male",
    halflingFemale: "Halfling Female",
  };

  const rollName = useCallback((raceKey) => {
    const names = data[raceKey];
    if (!names?.length) return;
    const name = names[Math.floor(Math.random() * names.length)];
    setRandomResult(`${raceLabels[raceKey]}: ${name}`);
  }, [data]);

  const rollFull = useCallback(() => {
    const genderRaces = ["humanMale", "humanFemale", "elfMale", "elfFemale", "dwarfMale", "dwarfFemale", "halflingMale", "halflingFemale"];
    const raceKey = genderRaces[Math.floor(Math.random() * genderRaces.length)];
    const firstName = data[raceKey][Math.floor(Math.random() * data[raceKey].length)];
    const hasLast = raceKey.startsWith("human") && data.humanLast?.length;
    const lastName = hasLast ? ` ${data.humanLast[Math.floor(Math.random() * data.humanLast.length)]}` : "";
    setRandomResult(`${raceLabels[raceKey]}: ${firstName}${lastName}`);
  }, [data]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <Button
          onClick={rollFull}
          size="sm"
          className="bg-primary/20 text-primary hover:bg-primary/30 rounded-sm"
          data-testid="randomize-names-full"
        >
          <Dices className="w-4 h-4 mr-1" /> Random Name
        </Button>
      </div>
      <RandomResult result={randomResult} />
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(data).map(([raceKey, names]) => (
          <div key={raceKey} className="bg-muted/20 p-3 rounded-sm">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-primary uppercase tracking-wider">{raceLabels[raceKey] || raceKey}</h4>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => rollName(raceKey)}
                data-testid={`roll-${raceKey}`}
              >
                <Dices className="w-3 h-3" />
              </Button>
            </div>
            <div className="space-y-0.5">
              {names.map((name, i) => (
                <p key={i} className="text-xs text-muted-foreground">{name}</p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DMScreenPage = () => {
  const { campaignId } = useParams();

  return (
    <Layout campaignName="DM Screen">
      <div data-testid="dm-screen-page">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-serif text-foreground">DM Screen</h1>
          <p className="text-muted-foreground text-sm">Quick reference tables and random generators for your session</p>
        </div>

        <TierGate feature="DM Screen" description="Access quick reference tables, random generators, and encounter tools to run your sessions smoothly.">
        <EncounterGenerator />

        <div className="space-y-3">
          {sections.map((section) => (
            <CollapsibleSection
              key={section.key}
              title={section.title}
              sectionKey={section.key}
              defaultOpen={false}
            >
              {section.type === "list" && (
                <ListSection data={dmScreenData[section.key]} sectionKey={section.key} />
              )}

              {section.type === "names" && (
                <NamesSection data={dmScreenData.names} />
              )}

              {section.type === "table" && section.key === "travelPace" && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 pr-4 text-primary font-medium">Pace</th>
                        <th className="text-left py-2 pr-4 text-primary font-medium">Per Minute</th>
                        <th className="text-left py-2 pr-4 text-primary font-medium">Per Hour</th>
                        <th className="text-left py-2 pr-4 text-primary font-medium">Per Day</th>
                        <th className="text-left py-2 text-primary font-medium">Effect</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dmScreenData.travelPace.map((row, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-2 pr-4 text-foreground font-medium">{row.pace}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{row.perMinute}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{row.perHour}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{row.perDay}</td>
                          <td className="py-2 text-muted-foreground">{row.effect}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {section.type === "table" && section.key === "services" && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 pr-4 text-primary font-medium">Service</th>
                        <th className="text-left py-2 text-primary font-medium">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dmScreenData.services.map((row, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-2 pr-4 text-foreground">{row.service}</td>
                          <td className="py-2 text-muted-foreground">{row.cost}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {section.type === "table" && section.key === "difficultyClasses" && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 pr-4 text-primary font-medium">Difficulty</th>
                        <th className="text-left py-2 text-primary font-medium">DC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dmScreenData.difficultyClasses.map((row, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-2 pr-4 text-foreground">{row.difficulty}</td>
                          <td className="py-2 text-primary font-bold">{row.dc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {section.type === "pricing" && (
                <div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 pr-4 text-primary font-medium">Rarity</th>
                          <th className="text-left py-2 text-primary font-medium">Price Range</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(dmScreenData.magicItemPricing)
                          .filter(([k]) => k !== "note")
                          .map(([rarity, price], i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="py-2 pr-4 text-foreground capitalize">{rarity.replace(/([A-Z])/g, ' $1').trim()}</td>
                              <td className="py-2 text-muted-foreground">{price}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 italic">{dmScreenData.magicItemPricing.note}</p>
                </div>
              )}
            </CollapsibleSection>
          ))}
        </div>
        </TierGate>
      </div>
    </Layout>
  );
};

export default DMScreenPage;
